// Guest session bootstrap, 23/09/2026.
//
// WHY THIS EXISTS
//
// The public pages - /StationList, /dashboard?s=, /instant?s= - are reachable
// without logging in, and they call charge-stations, station/:id, get-point/:id
// and connector-state. Those are open today only because the backend runs with
// REQUIRE_CUSTOMER_TOKEN=false, which leaves the gate in front of them
// log-only. The moment that switch is turned on they answer 401, the site's
// error handler treats that as "clear storage and go to login", and public
// browsing stops working.
//
// The mobile app already solved this: on first launch, with no stored session,
// it asks the backend for a GUEST token and keeps it. A guest token identifies
// nobody and unlocks nothing private - the ownership guards still refuse another
// customer's data with a 403 - it simply proves the caller is a real client.
//
// This does the same for the website, so the switch can be turned on without
// taking the public pages down. It is deliberately harmless while the switch is
// off: if a token already exists, this does nothing at all.
import axios from "axios";
import WebService from "./WebService";

let inFlight: Promise<void> | null = null;

const newGuestId = (): string => {
  try {
    if (typeof crypto !== "undefined" && typeof (crypto as any).randomUUID === "function") {
      return (crypto as any).randomUUID();
    }
  } catch (e) {
    // fall through to the timestamp form below
  }
  return `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

/**
 * Makes sure there is SOME token in localStorage before the public pages start
 * calling the API. Never replaces a real logged-in token, and never throws -
 * with the backend switch off the pages work with or without it, so a failure
 * here must not stop the site rendering.
 */
export const ensureGuestSession = async (): Promise<void> => {
  if (localStorage.getItem("token")) {
    return; // already logged in, or already has a guest token
  }
  if (inFlight) {
    return inFlight; // several pages mount at once; only ask once
  }

  inFlight = (async () => {
    try {
      // Kept as a bare axios call rather than WebService.postAPI: that helper
      // routes failures through the global error handler, which redirects on
      // 401 - exactly what this is here to avoid.
      const res = await axios.post(
        `${WebService.getBaseUrl()}customer/login`,
        { type: "guest", guest_id: newGuestId() },
        { headers: { "Content-Type": "application/json" } },
      );
      const token = res?.data?.token;
      // 24/09/2026: re-check AFTER the request, not only before it. The earlier
      // version tested for a token, went away for a network round trip, and then
      // wrote unconditionally - so a customer who finished the OTP login while
      // this was in flight had their real token overwritten by an anonymous
      // guest one, landing them on a different customers row and collecting a
      // 403 on /booking after Razorpay had already taken the payment.
      if (token && !localStorage.getItem("token")) {
        localStorage.setItem("token", token);
        localStorage.setItem("is_guest", "1");
      }
    } catch (e) {
      // Offline, or an older backend: leave it. The public pages still work
      // while REQUIRE_CUSTOMER_TOKEN is off, and a real login replaces this.
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
};

/** True when the only session we hold is an anonymous guest one. */
export const isGuestSession = (): boolean =>
  Boolean(localStorage.getItem("token")) && localStorage.getItem("is_guest") === "1";
